import React, { useEffect } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Card from '../../../components/Card';
import { useForm } from 'react-hook-form';
import { WindowInformation } from '../../../services/window-information';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import MyAppText from '../../../components/MyAppText';
import { citizenService } from '../../../services/central-api/citizen';

interface Props {
    isModalVisible: boolean;
    citizenBlockReasonId: number;
    onClose: () => void;
}

const defaultEmptyValues: ReasonFormObject = {
    description: ''
};

export default function CitizenBlockReasonModal({ isModalVisible, onClose, citizenBlockReasonId }: Props) {
    const { user: authenticatedUser } = useAuth();
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const queryResult = useQuery({
        queryKey: ['getCitizenBlockReasonById', citizenBlockReasonId, isModalVisible],
        queryFn: () => citizenService.getCitizenBlockReasonById(citizenBlockReasonId!),
        enabled: !!authenticatedUser && citizenBlockReasonId > 0 && isModalVisible,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data } = queryResult;

    async function setForm(data: CitizenBlockReason) {
        form.reset({
            description: data.description
        });
    }

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        setForm(data);

    }, [data, authenticatedUser]);

    const form = useForm<ReasonFormObject>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    async function formSubmit() {
        try {
            const data = form.getValues();
            if (citizenBlockReasonId > 0) {
                await citizenService.updateCitizenBlockReason({
                    ...data,
                    id: citizenBlockReasonId
                });
            } else {
                await citizenService.createCitizenBlockReason(data);
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            onClose();
            clearValues();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <Card style={{ padding: 0 }}>
                        {isLoading ?
                            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                                <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                            </View>
                            : <>

                                <View style={{ flexDirection: 'column', rowGap: 8, paddingBottom: 24 }}>
                                    <MyAppText style={styles.header}>{translate('registerReasons')}</MyAppText>
                                    <MyAppText style={styles.subheader}>{translate('fillTheFieldsToRegister')}</MyAppText>
                                </View>
                                <FormInput
                                    form={form}
                                    fieldName='description'
                                    label={translate('title')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={{ gap: 8 }}
                                />
                                <FormActions
                                    viewStyle={{ flex: 1, justifyContent: 'space-around', paddingTop: 24 }}
                                    buttonStyle={{ paddingHorizontal: 9, paddingVertical: 9 }}
                                    buttonContentContainerStyle={{ flex: 1, }}
                                    textStyle={{ fontSize: 16, fontWeight: '600' }}
                                    onSubmit={formSubmit}
                                    onClose={() => {
                                        onClose();
                                        clearValues();
                                    }}
                                    disabled={!form.formState.isValid} />

                            </>}
                    </Card>
                </View>
            </View>
        </Modal >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 32,
        padding: 32
    }),
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    itemView: (flex: number) => ({
        gap: 8,
        flex
    }),
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
}));
